import React from "react";
import api from "../api/client";
import { ModalShell, MaterialTemplateViewer } from "./ui-helpers";

function materialTitle(item) {
  const name = item?.material || item?.material_name || item?.["Material Name"] || "Material";
  return `${name} template`;
}

function materialSubtitle(item) {
  const parts = [];
  if (item?.vendor) parts.push(`Vendor: ${item.vendor}`);
  if (item?.sku) parts.push(`SKU: ${item.sku}`);
  return parts.join(" • ");
}

function chainLabel(entry, fallbackItem) {
  const material = entry?.material || fallbackItem || {};
  const name = material.material || material.material_name || material["Material Name"] || "Material";
  const version = entry?.version ?? material.version ?? 0;
  return `${name}${entry?.current ? " (current" : ""}${entry?.current ? ` — version ${version})` : ` (version ${version})`}`;
}

function specsFromChainEntry(entry) {
  const tpl = entry?.template || {};
  return Array.isArray(tpl.Specifications) ? tpl.Specifications : [];
}

export function useMaterialTemplateModal() {
  const [state, setState] = React.useState({
    open: false,
    loading: false,
    error: "",
    specs: [],
    item: null,
    title: "Material Template",
    subtitle: "",
    lineageChain: [],
    selectedIndex: 0,
  });

  const closeTemplate = React.useCallback(() => {
    setState((current) => ({ ...current, open: false, loading: false }));
  }, []);

  const openTemplate = React.useCallback(async (item) => {
    if (!item) return;
    const title = materialTitle(item);
    const subtitle = materialSubtitle(item);
    setState({
      open: true,
      loading: true,
      error: "",
      specs: [],
      item,
      title,
      subtitle,
      lineageChain: [],
      selectedIndex: 0,
    });
    try {
      const response = await api.get("/materials/template", {
        params: {
          material_id: item?.material_id || item?.Material_ID || item?.id || undefined,
          vendor: item?.vendor || undefined,
          sku: item?.sku || undefined,
        },
      });
      const template = response.data?.template || {};
      const chain = Array.isArray(response.data?.lineage_chain) ? response.data.lineage_chain : [];
      const selectedIndex = Math.max(0, chain.findIndex((entry) => entry?.current));
      setState({
        open: true,
        loading: false,
        error: "",
        specs: Array.isArray(template.Specifications) ? template.Specifications : [],
        item: response.data?.material || item,
        title,
        subtitle,
        lineageChain: chain,
        selectedIndex: selectedIndex >= 0 ? selectedIndex : 0,
      });
    } catch (err) {
      setState({
        open: true,
        loading: false,
        error: err?.response?.data?.error || "Unable to load material template.",
        specs: [],
        item,
        title,
        subtitle,
        lineageChain: [],
        selectedIndex: 0,
      });
    }
  }, []);

  return { state, openTemplate, closeTemplate };
}

export function MaterialTemplateModal({ state, onClose }) {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  React.useEffect(() => {
    setSelectedIndex(Number(state?.selectedIndex || 0));
  }, [state?.open, state?.selectedIndex]);

  const hasChain = Array.isArray(state?.lineageChain) && state.lineageChain.length > 1;
  const selectedEntry = hasChain ? state.lineageChain[selectedIndex] || state.lineageChain[0] : null;
  const selectedSpecs = hasChain ? specsFromChainEntry(selectedEntry) : (state?.specs || []);

  return (
    <ModalShell
      open={!!state?.open}
      title={state?.title || "Material Template"}
      subtitle={state?.subtitle || ""}
      size="xl"
      className="material-template-modal"
      onClose={onClose}
    >
      {state?.loading ? <div className="helper-text">Loading template…</div> : null}
      {!state?.loading && state?.error ? <div className="banner error">{state.error}</div> : null}
      {!state?.loading && !state?.error ? (
        <>
          {hasChain ? (
            <div className="template-version-selector">
              <label>
                <span>Version</span>
                <select value={selectedIndex} onChange={(event) => setSelectedIndex(Number(event.target.value) || 0)}>
                  {state.lineageChain.map((entry, index) => (
                    <option key={`${entry?.material?.material_id || index}`} value={index}>
                      {chainLabel(entry, state.item)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}
          <MaterialTemplateViewer specs={selectedSpecs} />
        </>
      ) : null}
    </ModalShell>
  );
}
